import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { showToast } from "./toast";
import type { PauseAndAskState, TogetherMediaType, TogetherSchedule, TogetherSession, TogetherSessionHistoryEntry } from "./types";
import type {
  TogetherAcceptedData,
  TogetherDeclinedData,
  TogetherEndedData,
  TogetherInviteData,
  TogetherPartnerDisconnectedData,
  TogetherPartnerReconnectedData,
  TogetherPauseAskAnsweredData,
  TogetherPauseAskEndedData,
  TogetherPauseAskStartedData,
  TogetherReactionData,
  TogetherReactionHoldData,
  TogetherReactionHoldEndData,
  TogetherScheduleCancelledData,
  TogetherScheduleCreatedData,
  TogetherScheduleReadyData,
  TogetherScheduleStartedData,
  TogetherScheduleUpdatedData,
} from "./chatSocket";

// Framework-agnostic Together Mode data layer, same shape as giftsApi.ts -
// React-only feature, appState/notifyStateChange used for consistency with
// every other list/session in this app. `together_sync` is deliberately
// NOT handled here: applying a remote sync touches the actual <video>
// element and Lightbox navigation state directly, which belongs in
// togetherSync.ts (built alongside the Lightbox integration), not this
// framework-agnostic data layer.

export type TogetherResult<T extends object = { session?: never }> = ({ ok: true } & T) | { ok: false; error?: string };

export async function inviteTogetherApi(params: {
  recipientEmail?: string;
  filename: string;
  mediaType: TogetherMediaType;
}): Promise<TogetherResult<{ session: TogetherSession }>> {
  const res = await apiFetch("/api/together/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.togetherSession = data.session as TogetherSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function acceptTogetherApi(sessionId: string): Promise<TogetherResult<{ session: TogetherSession }>> {
  const res = await apiFetch(`/api/together/${sessionId}/accept`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.togetherIncomingInvite = null;
  appState.togetherSession = data.session as TogetherSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function declineTogetherApi(sessionId: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/decline`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.togetherIncomingInvite = null;
  notifyStateChange();
  return { ok: true };
}

// Fire-and-forget from the caller's perspective (togetherSync.ts throttles
// calls itself) - a 429 here was long assumed to just mean "try again next
// tick," safely ignorable. That's true for ongoing playback position drift
// (the periodic interval covers it), but NOT for a discrete play/pause
// transition: pausing stops that interval immediately, so if the pause
// sync itself lands inside the server's 500ms window and gets 429'd, there
// is no "next tick" left to ever deliver it - the peer can be left
// watching a video that's actually paused locally, with no further
// correction until some other action happens to fire a new sync. See
// togetherSync.ts's retry-once-past-the-window handling for discrete
// events, which this rateLimited flag exists to support.
export async function syncTogetherApi(
  sessionId: string,
  params: { filename: string; mediaType: TogetherMediaType; position: number; playing: boolean },
): Promise<{ ok: true } | { ok: false; rateLimited?: boolean }> {
  const res = await apiFetch(`/api/together/${sessionId}/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 429) return { ok: false, rateLimited: true };
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

// Renders the sender's own burst immediately (optimistic - no round trip
// wait) rather than waiting for a server echo, since the server only ever
// pushes together_reaction to the OTHER participant.
export async function reactTogetherApi(sessionId: string, emoji: string): Promise<TogetherResult> {
  appState.togetherReactions = [...appState.togetherReactions, { id: crypto.randomUUID(), emoji, fromUserId: appState.currentUserEmail }];
  notifyStateChange();

  const res = await apiFetch(`/api/together/${sessionId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

// Press-and-hold reactions - a continuous stream, not a one-shot event like
// reactTogetherApi above. The sender's own escalating visual is rendered
// entirely from local component state in TogetherRoot.tsx (zero round-trip,
// same "own side renders optimistically" asymmetry as reactTogetherApi),
// so unlike reactTogetherApi this has no local appState write of its own -
// it's purely the outgoing half of the protocol.
export async function reactHoldApi(sessionId: string, emoji: string, intensity: number): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/react-hold`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji, intensity }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

export async function reactHoldEndApi(sessionId: string, emoji: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/react-hold-end`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

// Read-only lookup of this user's current session state, straight from the
// server (not appState) - used by togetherSync.ts's resyncAfterReconnect,
// which needs ground truth after a socket drop, not whatever this client
// last believed before it went offline.
export async function fetchTogetherSessionApi(): Promise<TogetherSession | null> {
  const res = await apiFetch("/api/together/session");
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return (data.session as TogetherSession | null) ?? null;
}

export async function leaveTogetherApi(sessionId: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/leave`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.togetherSession = null;
  appState.togetherAwaitingRejoin = false;
  notifyStateChange();
  return { ok: true };
}

// Explicit, state-changing rejoin - used by TogetherRejoinPill's tap handler
// (unlike fetchTogetherSessionApi above, which stays read-only/side-effect-
// free so it's safe to call on every reconnect). A 404 here means the
// session ended between the pill showing and the tap landing - mapped to a
// user-facing message here so the caller never has to invent its own copy
// for that race.
export async function rejoinTogetherApi(sessionId: string): Promise<TogetherResult<{ session: TogetherSession }>> {
  const res = await apiFetch(`/api/together/${sessionId}/rejoin`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error === "not_found" ? "This Watch Together session has ended." : data.message || data.error };
  }
  return { ok: true, session: data.session as TogetherSession };
}

// Read-only, fetched on demand (TogetherInviteDialog.tsx, when its history
// section is opened) rather than kept live in appState - past sessions
// don't change once written, so there's no push event to react to, just a
// plain GET like fetchGameDecksApi's static-reference-data pattern.
export async function fetchTogetherHistoryApi(): Promise<TogetherSessionHistoryEntry[]> {
  const res = await apiFetch("/api/together/history");
  if (res.status === 401) {
    requestLock("unauthorized");
    return [];
  }
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return (data.history as TogetherSessionHistoryEntry[]) ?? [];
}

export function removeTogetherReaction(id: string): void {
  appState.togetherReactions = appState.togetherReactions.filter((r) => r.id !== id);
  notifyStateChange();
}

// --- Scheduled Watch Together ---

export async function scheduleTogetherApi(params: {
  recipientEmail?: string;
  filename: string;
  startsAt: string;
}): Promise<TogetherResult<{ schedule: TogetherSchedule }>> {
  const res = await apiFetch("/api/together/schedule", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.togetherSchedules = [data.schedule as TogetherSchedule, ...appState.togetherSchedules];
  notifyStateChange();
  return { ok: true, schedule: data.schedule };
}

// Read-only, straight from the server - used both for the initial load
// (AuthGate.tsx, same pattern as loadGiftsData()) and after any mutation
// where refetching the whole list is simpler than patching appState by
// hand (schedules aren't a high-frequency-update live session the way a
// togetherSession is).
export async function fetchTogetherSchedulesApi(): Promise<TogetherSchedule[]> {
  const res = await apiFetch("/api/together/schedules");
  if (res.status === 401) {
    requestLock("unauthorized");
    return [];
  }
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return (data.schedules as TogetherSchedule[]) ?? [];
}

export async function loadTogetherSchedulesData(): Promise<void> {
  appState.togetherSchedules = await fetchTogetherSchedulesApi();
  notifyStateChange();
}

export async function updateTogetherScheduleApi(scheduleId: string, startsAt: string): Promise<TogetherResult<{ schedule: TogetherSchedule }>> {
  const res = await apiFetch(`/api/together/schedules/${scheduleId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ startsAt }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === scheduleId ? (data.schedule as TogetherSchedule) : s));
  notifyStateChange();
  return { ok: true, schedule: data.schedule };
}

export async function cancelTogetherScheduleApi(scheduleId: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/schedules/${scheduleId}/cancel`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === scheduleId ? { ...s, status: "cancelled" as const } : s));
  notifyStateChange();
  return { ok: true };
}

export async function startScheduledTogetherApi(scheduleId: string): Promise<TogetherResult<{ session: TogetherSession }>> {
  const res = await apiFetch(`/api/together/schedules/${scheduleId}/start-now`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.togetherSession = data.session as TogetherSession;
  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === scheduleId ? { ...s, status: "started" as const } : s));
  notifyStateChange();
  return { ok: true, session: data.session };
}

export function applyTogetherScheduleCreated(data: TogetherScheduleCreatedData): void {
  // The push doesn't carry the full viewer-relative schedule shape (that
  // would need togetherScheduleResponse-equivalent work server-side just
  // for a notification payload) - simplest correct approach is to refetch
  // the list, same as any other "something changed, resync" moment.
  loadTogetherSchedulesData();
  showToast(`${data.creatorName} scheduled a Watch Together.`, "info");
}

export function applyTogetherScheduleUpdated(data: TogetherScheduleUpdatedData): void {
  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === data.scheduleId ? { ...s, startsAt: data.startsAt, status: data.status, isLocked: data.status === "scheduled" } : s));
  notifyStateChange();
}

export function applyTogetherScheduleCancelled(data: TogetherScheduleCancelledData): void {
  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === data.scheduleId ? { ...s, status: "cancelled" as const } : s));
  notifyStateChange();
}

export function applyTogetherScheduleReady(data: TogetherScheduleReadyData): void {
  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === data.scheduleId ? { ...s, status: "ready" as const, isLocked: false } : s));
  appState.togetherScheduleReminder = { scheduleId: data.scheduleId, peerName: data.peerName, filename: data.filename, mediaType: "video", startsAt: data.startsAt };
  notifyStateChange();
}

export function applyTogetherScheduleStarted(data: TogetherScheduleStartedData): void {
  appState.togetherSession = data.session as TogetherSession;
  appState.togetherSchedules = appState.togetherSchedules.map((s) => (s.id === data.scheduleId ? { ...s, status: "started" as const } : s));
  notifyStateChange();
}

// --- Pause & Ask ---
// A lightweight in-context Q&A layer, not a second Game feature - draws
// from gameDecks.js's existing deck data server-side (or a custom
// question), pauses playback via the session's own filename/mediaType/
// position fields (the SAME fields /sync already uses - see
// togetherSync.ts's applyRemoteSync, reused directly by the WS handler
// rather than reimplemented here), and posts through the same shared
// chat-message primitive Game's own card-announcement already uses.

export async function pauseAskStartApi(
  sessionId: string,
  params: {
    source: "custom" | "random" | "questions" | "truth_or_dare" | "spicy";
    intensity?: number;
    customText?: string;
    position: number;
    filename: string;
    mediaType: TogetherMediaType;
  },
): Promise<TogetherResult<{ pauseAndAsk: PauseAndAskState }>> {
  const res = await apiFetch(`/api/together/${sessionId}/pause-ask/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.pauseAndAsk) {
    // Lost the race to a peer who started one moments earlier (not a real
    // concurrent race - see server.js's comment - just ordinary request
    // ordering) - the 409 body carries the winner's current state so this
    // client can render it immediately instead of just erroring.
    if (appState.togetherSession) {
      appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: data.pauseAndAsk as PauseAndAskState };
      notifyStateChange();
    }
    return { ok: false, error: data.message || data.error };
  }
  if (!res.ok) return { ok: false, error: data.message || data.error };

  if (appState.togetherSession) {
    appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: data.pauseAndAsk as PauseAndAskState };
    notifyStateChange();
  }
  return { ok: true, pauseAndAsk: data.pauseAndAsk };
}

export async function pauseAskAnswerApi(sessionId: string, text: string): Promise<TogetherResult<{ pauseAndAsk: PauseAndAskState }>> {
  const res = await apiFetch(`/api/together/${sessionId}/pause-ask/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  if (appState.togetherSession) {
    appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: data.pauseAndAsk as PauseAndAskState };
    notifyStateChange();
  }
  return { ok: true, pauseAndAsk: data.pauseAndAsk };
}

// Idempotent by design (server returns ok:true even if pauseAndAsk was
// already cleared by the peer's own resume/skip a moment earlier) - the
// caller must not infer "I resumed" from a bare ok:true response; the
// single source of truth for "what actually happened" is the
// pauseAndAsk: non-null -> null transition itself, wherever it's first
// observed (this call's own success, or the together_pause_ask_ended push -
// see TogetherRoot.tsx).
export async function pauseAskResumeApi(sessionId: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/pause-ask/resume`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  clearPauseAndAsk();
  return { ok: true };
}

export async function pauseAskSkipApi(sessionId: string): Promise<TogetherResult> {
  const res = await apiFetch(`/api/together/${sessionId}/pause-ask/skip`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  clearPauseAndAsk();
  return { ok: true };
}

function clearPauseAndAsk(): void {
  if (!appState.togetherSession || !appState.togetherSession.pauseAndAsk) return;
  appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: null };
  notifyStateChange();
}

// --- Apply incoming WS pushes to appState (called from the socket handler
// registry TogetherRoot.tsx sets up via setTogetherSocketHandlers) ---

export function applyTogetherInvite(data: TogetherInviteData): void {
  // Ignore a stray invite if this client is somehow already in a session
  // (the server itself already rejects double-invites via 409, but a
  // client can still race the push with its own local state).
  if (appState.togetherSession) return;
  appState.togetherIncomingInvite = data;
  notifyStateChange();
}

export function applyTogetherAccepted(data: TogetherAcceptedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherSession = { ...appState.togetherSession, status: "active", filename: data.filename, mediaType: data.mediaType };
  notifyStateChange();
}

export function applyTogetherDeclined(data: TogetherDeclinedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  showToast(`${appState.togetherSession.peerName} declined your invite.`, "info");
  appState.togetherSession = null;
  notifyStateChange();
}

export function applyTogetherEnded(data: TogetherEndedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  const peerName = appState.togetherSession.peerName;
  const wasPending = appState.togetherSession.status === "pending";
  if (data.reason === "disconnected") showToast(`${peerName} disconnected.`, "info");
  // "expired" now covers both an unanswered invite (wasPending) and the
  // rare outer TTL safety net on a long-active session - different copy
  // for each, using the session's own last-known status rather than a new
  // server-side reason string, since that's already enough to tell them
  // apart and every reason string is otherwise a public WS event contract.
  else if (data.reason === "expired") showToast(wasPending ? "Your invite expired." : "Watch Together session ended.", "info");
  else if (data.reason === "left") showToast(`${peerName} left Watch Together.`, "info");
  appState.togetherSession = null;
  appState.togetherAwaitingRejoin = false;
  notifyStateChange();
}

export function applyTogetherPartnerDisconnected(data: TogetherPartnerDisconnectedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherSession = { ...appState.togetherSession, partnerConnected: false };
  notifyStateChange();
}

export function applyTogetherPartnerReconnected(data: TogetherPartnerReconnectedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherSession = { ...appState.togetherSession, partnerConnected: true };
  notifyStateChange();
}

export function applyTogetherReaction(data: TogetherReactionData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherReactions = [...appState.togetherReactions, { id: crypto.randomUUID(), emoji: data.emoji, fromUserId: data.fromUserId }];
  notifyStateChange();
}

// Updates the SAME slot in place (never recreated) so the escalating
// intensity animates smoothly rather than restarting - see
// appState.togetherHoldReaction's own comment in state.ts.
export function applyTogetherReactionHold(data: TogetherReactionHoldData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherHoldReaction = { emoji: data.emoji, fromUserId: data.fromUserId, intensity: data.intensity };
  notifyStateChange();
}

export function applyTogetherReactionHoldEnd(data: TogetherReactionHoldEndData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  const current = appState.togetherHoldReaction;
  if (!current || current.fromUserId !== data.fromUserId || current.emoji !== data.emoji) return;
  // "Letting go" plays the familiar rise-and-fade release animation via the
  // SAME one-shot appState.togetherReactions/ReactionBurst pathway tap
  // reactions already use, instead of a second bespoke animation.
  appState.togetherReactions = [
    ...appState.togetherReactions,
    { id: crypto.randomUUID(), emoji: current.emoji.repeat(current.intensity), fromUserId: data.fromUserId },
  ];
  appState.togetherHoldReaction = null;
  notifyStateChange();
}

// Only applies the pauseAndAsk half of the push - the actual video pause/
// seek is handled separately, by TogetherRoot.tsx's socket registration
// calling the EXISTING applyRemoteSync (togetherSync.ts) directly with
// this same event's filename/mediaType/position, rather than this
// framework-agnostic data layer reaching into the DOM itself.
export function applyTogetherPauseAskStarted(data: TogetherPauseAskStartedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  appState.togetherSession = {
    ...appState.togetherSession,
    pauseAndAsk: {
      id: data.chatMessageId,
      category: data.category,
      question: data.question,
      askedByUserId: data.askedByUserId,
      revealed: false,
      myAnswered: false,
      peerAnswered: false,
    },
  };
  notifyStateChange();
}

export function applyTogetherPauseAskAnswered(data: TogetherPauseAskAnsweredData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  const current = appState.togetherSession.pauseAndAsk;
  if (!current) return;

  if (data.revealed) {
    appState.togetherSession = {
      ...appState.togetherSession,
      pauseAndAsk: {
        id: current.id,
        category: current.category,
        question: current.question,
        askedByUserId: current.askedByUserId,
        revealed: true,
        answers: data.answers,
      },
    };
    notifyStateChange();
  } else if (!current.revealed) {
    // Only the peer's answered-state changes here - your OWN answer is
    // applied locally by pauseAskAnswerApi's own response, not by waiting
    // for a push (the server never echoes back to the actor).
    appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: { ...current, peerAnswered: true } };
    notifyStateChange();
  }
}

// Only ever fired by the WS push (the actor's own resume/skip clears the
// state locally via pauseAskResumeApi/pauseAskSkipApi's own response, not
// this function) - so it's always describing something the PEER did,
// making a toast here safe (no double-messaging risk, unlike the actor's
// own side - see pauseAskResumeApi's comment).
export function applyTogetherPauseAskEnded(data: TogetherPauseAskEndedData): void {
  if (!appState.togetherSession || appState.togetherSession.id !== data.sessionId) return;
  if (!appState.togetherSession.pauseAndAsk) return;
  appState.togetherSession = { ...appState.togetherSession, pauseAndAsk: null };
  notifyStateChange();
  showToast(data.reason === "skipped" ? `${appState.togetherSession.peerName} skipped the question.` : "Back to watching ▶", "info");
}
